import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface TimelineItem {
  id: string;
  /** Mono meta row: date, reference, version. Rendered uppercase with middots. */
  meta: string[];
  /** Right-aligned --ok delta or status in the meta row. */
  delta?: string;
  deltaTone?: 'ok' | 'warn' | 'brand' | 'mute';
  title: ReactNode;
  summary?: ReactNode;
  byline?: string;
  nodeTone?: 'info' | 'ok' | 'warn' | 'brand' | 'dim';
}

const NODE: Record<NonNullable<TimelineItem['nodeTone']>, string> = {
  info: 'bg-info',
  ok: 'bg-ok',
  warn: 'bg-warn',
  brand: 'bg-brand',
  dim: 'bg-dim',
};

const DELTA: Record<NonNullable<TimelineItem['deltaTone']>, string> = {
  ok: 'text-ok',
  warn: 'text-warn',
  brand: 'text-brand-text',
  mute: 'text-mute',
};

export interface TimelineProps {
  items: TimelineItem[];
  className?: string;
}

/** Vertical rail with 8px nodes (DESIGN_SYSTEM section 8.9). */
export function Timeline({ items, className }: TimelineProps) {
  return (
    <ol className={cn('space-y-3 border-l border-hair pl-6', className)}>
      {items.map((item) => (
        <li key={item.id} className="relative">
          <span aria-hidden className={cn('absolute -left-[29px] top-1.5 h-2 w-2 rounded-full ring-2 ring-panel', NODE[item.nodeTone ?? 'info'])} />
          <div className="flex items-baseline justify-between gap-3 font-mono text-2xs uppercase tracking-[0.16em] text-mute">
            <span className="truncate">{item.meta.join(' · ')}</span>
            {item.delta ? <span className={cn('shrink-0', DELTA[item.deltaTone ?? 'ok'])}>{item.delta}</span> : null}
          </div>
          <p className="mt-1 text-body text-ink">{item.title}</p>
          {item.summary ? <p className="mt-0.5 text-nav leading-snug text-ink/75">{item.summary}</p> : null}
          {item.byline ? <p className="mt-1 text-xs text-mute">{item.byline}</p> : null}
        </li>
      ))}
    </ol>
  );
}
